import { resourceChildren } from './order';
export const operations = ['read', 'create', 'update', 'delete'];
export const operationNames = { read: '查看', create: '新增', update: '修改', delete: '删除' };
export const cloneCatalog = (catalog) => JSON.parse(JSON.stringify(catalog));
export function resourceTree(catalog, search = '') {
    const resources = catalog?.resources || {};
    const build = (parent, ancestors = new Set()) => Object.entries(resources)
        .filter(([, node]) => (node.parent || '') === parent)
        .sort(([a, x], [b, y]) => x.sort - y.sort || a.localeCompare(b))
        .flatMap(([key, node]) => {
            if (ancestors.has(key)) return [];
            const children = build(key, new Set([...ancestors, key]));
            if (search && !`${key} ${node.name}`.toLowerCase().includes(search.toLowerCase()) && !children.length) return [];
            return [{ key, title: node.name, children, isLeaf: !children.length }];
        });
    return build('');
}
export function catalogDiff(before, after, path = '') {
    if (JSON.stringify(before) === JSON.stringify(after)) return [];
    if (before && after && typeof before === 'object' && typeof after === 'object' && !Array.isArray(before) && !Array.isArray(after)) {
        return [...new Set([...Object.keys(before), ...Object.keys(after)])].sort().flatMap(key => catalogDiff(before[key], after[key], path ? `${path}.${key}` : key));
    }
    return [{ path, before, after, kind: before === undefined ? '新增' : after === undefined ? '删除' : '修改' }];
}
export function renameResource(catalog, oldKey, newKey) {
    if (!catalog.resources[oldKey] || (oldKey !== newKey && catalog.resources[newKey])) throw new Error('资源标识重复或不存在');
    const next = cloneCatalog(catalog);
    next.resources[newKey] = next.resources[oldKey];
    if (oldKey !== newKey) {
        delete next.resources[oldKey];
        Object.values(next.resources).forEach(node => {
            if (node.parent === oldKey) node.parent = newKey;
            if (node.child_order) node.child_order = node.child_order.map(item => ({ ...item, key: item.key === oldKey ? newKey : item.key }));
            if (node.references) node.references = node.references.map(key => key === oldKey ? newKey : key);
            if (node.reference_operations?.[oldKey]) { node.reference_operations[newKey] = node.reference_operations[oldKey]; delete node.reference_operations[oldKey]; }
        });
    }
    return next;
}
export function renameFeature(catalog, oldKey, newKey) {
    if (!catalog.features[oldKey] || (oldKey !== newKey && catalog.features[newKey])) throw new Error('功能标识重复或不存在');
    const next = cloneCatalog(catalog);
    next.features[newKey] = next.features[oldKey];
    if (oldKey !== newKey) {
        delete next.features[oldKey];
        Object.values(next.resources).forEach(node => Object.values(node.operations || {}).forEach(op => {
            op.feature_keys = op.feature_keys.map(key => key === oldKey ? newKey : key);
        }));
    }
    return next;
}
export function bindingKey(binding) { return `${binding.resource}:${binding.operation}`; }
export function mergeBindings(...lists) {
    const result = new Map();
    lists.flat().forEach(binding => {
        const key = bindingKey(binding);
        const previous = result.get(key);
        result.set(key, { ...binding, features: [...new Set([...(previous?.features || []), ...(binding.features || [])])] });
    });
    return [...result.values()];
}

export function defaultBindings(catalog) {
    if (!catalog) return [];
    const groups = new Set(catalog.groups.filter(group => group.default).map(group => group.key));
    return mergeBindings(Object.entries(catalog.resources).flatMap(([resource, node]) => {
        let current = node;
        const visited = new Set();
        while (!current.group && current.parent && !visited.has(current.parent)) {
            visited.add(current.parent); current = catalog.resources[current.parent] || {};
        }
        if (!groups.has(current.group)) return [];
        if (node.kind === 'resource') return directoryBindings(catalog, resource);
        return (node.references || []).flatMap(target => directoryBindings(catalog, target, node.reference_operations?.[target] || operations));
    }));
}

export const hasAllPermission = (catalog, bindings = []) => bindings.some(binding => {
    const node = catalog?.resources?.[binding.resource];
    const definition = node?.operations?.[binding.operation];
    return node?.builtin === 'all' && node.kind === 'resource' && binding.features?.some(feature => definition?.feature_keys?.includes(feature));
});

export function resourceReachable(catalog, from, target, references = true, seen = new Set()) {
    if (from === target) return true;
    if (seen.has(from)) return false;
    seen.add(from);
    const children = Object.entries(catalog.resources).filter(([, node]) => node.parent === from).map(([key]) => key);
    return [...children, ...(references ? catalog.resources[from]?.references || [] : [])].some(key => resourceReachable(catalog, key, target, references, seen));
}
export function canReference(catalog, owner, target) {
    return Boolean(catalog.resources[target]) && !resourceReachable(catalog, owner, target, false) && !resourceReachable(catalog, target, owner);
}
export function directoryBindings(catalog, key, allowed = operations, seen = new Set()) {
    const node = catalog?.resources?.[key];
    if (!node || seen.has(key)) return [];
    const own = node.kind === 'resource' ? allowed.filter(operation => node.operations?.[operation]).map(operation => ({ resource: key, operation, features: [...node.operations[operation].feature_keys] })) : [];
    const next = new Set([...seen, key]);
    return mergeBindings(
        own,
        ...Object.entries(catalog.resources).filter(([, child]) => child.parent === key).map(([child]) => directoryBindings(catalog, child, allowed, next)),
        ...(node.references || []).map(target => directoryBindings(catalog, target, allowed.filter(operation => !node.reference_operations?.[target] || node.reference_operations[target].includes(operation)), next)),
    );
}
export function referenceTree(catalog, search = '') {
    const query = search.trim().toLowerCase();
    const walk = (key, path, allowed = operations, seen = new Set(), matching = false, owner = '') => {
        const node = catalog.resources[key];
        if (!node || seen.has(key)) return [];
        const matches = matching || !query || `${key} ${node.name}`.toLowerCase().includes(query);
        const next = new Set([...seen, key]);
        const children = resourceChildren(catalog, key).flatMap(item => item.kind === 'reference'
            ? walk(item.key, `${path}/@${item.key}`, allowed.filter(operation => !node.reference_operations?.[item.key] || node.reference_operations[item.key].includes(operation)), next, matches, key)
            : walk(item.key, `${path}/${item.key}`, allowed, next, matches));
        if (!matches && !children.length) return [];
        return [{ key: path, resource: key, referenceOwner: owner, title: node.name, kind: node.kind, allowed, children, isLeaf: !children.length }];
    };
    return resourceChildren(catalog).flatMap(({ key }) => walk(key, key));
}

export function bindingSelectionState(bindings, granted, all = false) {
    const checked = bindings.length > 0 && (all || bindings.every(binding => {
        const current = granted.get(bindingKey(binding));
        return current && binding.features.every(feature => current.features.includes(feature));
    }));
    const partial = !checked && bindings.some(binding => granted.has(bindingKey(binding)));
    return { checked, partial };
}

export function canonicalTreeKey(catalog, key) {
    const parts = [key]; const seen = new Set(parts);
    let parent = catalog?.resources?.[key]?.parent;
    while (parent && !seen.has(parent)) { parts.unshift(parent); seen.add(parent); parent = catalog.resources[parent]?.parent; }
    return parts.join('/');
}


// Picker candidates use canonical tree order, with each resource appearing once.
// Reference aliases do not change a target's position in its owning directory.
export function resourceOptions(catalog) {
    const visited = new Set();
    const walk = (parent, path) => resourceChildren(catalog, parent).flatMap(item => {
        if (item.kind !== 'node' || visited.has(item.key)) return [];
        visited.add(item.key);
        const node = catalog.resources[item.key];
        const names = [...path, node.name];
        return [{ value: item.key, label: `${names.join(' / ')} · ${item.key}` }, ...walk(item.key, names)];
    });
    return walk('', []);
}
